import { zodResolver } from "@hookform/resolvers/zod";
import { propertyFieldsSchema } from "../schemas/property-fields-schema";
import { useForm } from "react-hook-form";
import { PropertyFieldsFormData } from "../schemas/property-fields-schema";
import { useCallback, useState } from "react";
import { transformFormDataToApiPayload } from "../utils/property-fields-transformer";
import { PropertyFieldValueViewModel } from "../types/property-field-value.view-model";
import { useUpdatePropertyFields } from "components/properties/mutates/use-update-property-fields";
import { createSectionsFromFormData } from "../utils/create-default-property-fields-view-fields";

type UsePropertyFieldsFormProps = {
  values: PropertyFieldValueViewModel[];
  propertyId: number;
  onSuccess?: () => void;
  onError?: () => void;
};

export const usePropertyFieldsForm = ({
  values,
  propertyId,
  onSuccess,
  onError,
}: UsePropertyFieldsFormProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const updatePropertyFields = useUpdatePropertyFields();

  const form = useForm<PropertyFieldsFormData>({
    resolver: zodResolver(propertyFieldsSchema),
    mode: "onChange",
    reValidateMode: "onBlur",
    shouldUnregister: false,
  });

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setIsSubmitting(true);

      try {
        await form.handleSubmit(
          async data => {
            const currentSections = createSectionsFromFormData(data);
            const apiPayload = transformFormDataToApiPayload(
              data,
              currentSections.flatMap(section => section.fields),
              values,
              propertyId
            );

            await updatePropertyFields.mutateAsync(apiPayload);
            onSuccess?.();
          },
          errors => {
            console.log("Form validation errors:", { errors, e });
            // Keep the form in editing mode if there are errors
            onError?.();
          }
        )(e);
      } finally {
        setIsSubmitting(false);
      }
    },
    [form, values, propertyId, updatePropertyFields, onSuccess, onError]
  );

  return {
    form,
    handleSubmit,
    isSubmitting,
  };
};
